/* ══════════════════════════════════════════════════════════════════════
   ACTIONS MODULE — USES BACKEND TRUTH
   
   The backend's buildActionQueue() in routes.ts already produces a
   deterministic, deduped, priority-sorted action queue. It ships in
   the /api/alerts response as `actions[]`.
   
   This module renders that queue directly. Zero client-side derivation.
   ══════════════════════════════════════════════════════════════════════ */

import { ModuleShell } from '../components/ModuleShell';
import { StatusChip, EmptyState } from '../components/StatusChip';

import type { AlertsResponse, BackendAction, ModuleState } from '../api/types';

function priorityVariant(p: number) {
  if (p <= 1) return 'error' as const;
  if (p <= 2) return 'warning' as const;
  if (p <= 3) return 'info' as const;
  return 'muted' as const;
}

function priorityColor(p: number): string {
  if (p <= 1) return 'var(--red)';
  if (p <= 2) return 'var(--yellow)';
  if (p <= 3) return 'var(--blue)';
  return 'var(--text-ghost)';
}

interface Props {
  moduleState: ModuleState<AlertsResponse> & { refetch: () => void };
}

export function ActionsModule({ moduleState }: Props) {
  const actions = moduleState.data?.actions || [];

  return (
    <ModuleShell name="actions" title="Recommended Actions"
      status={moduleState.status} error={moduleState.error}
      dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt}
      isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason}
      onRetry={moduleState.refetch}
      headerRight={<StatusChip label={`${actions.length} actions`} variant={actions.length > 0 ? 'warning' : 'success'} showIcon />}>

      {actions.length === 0 ? (
        <EmptyState icon="✓" title="No actions needed" sub="All clients are on track or have no open alerts." />
      ) : (
        actions.map((a, i) => <ActionCard key={`${a.client_id}-${i}`} action={a} />)
      )}
    </ModuleShell>
  );
}

function ActionCard({ action }: { action: BackendAction }) {
  const color = priorityColor(action.priority);

  return (
    <div style={{
      padding: '14px 16px', background: 'var(--bg-surface)',
      borderLeft: `3px solid ${color}`,
      borderRadius: '0 var(--radius-md) var(--radius-md) 0', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {action.client_name || action.client_id}
            </span>
            <StatusChip label={`P${action.priority}`} variant={priorityVariant(action.priority)} showIcon />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            → {action.recommendation}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, color: 'var(--text-ghost)' }}>
            <span>Trigger: {action.trigger}</span>
            <span>Owner: <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{action.owner}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
